import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Star, Clock, Truck, MapPin } from '../../lib/lucide-proxy';
import { supabase } from '../../config/supabase';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';

const FeaturedBreweries = () => {
  const [breweries, setBreweries] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedBreweries();
  }, []);

  const fetchFeaturedBreweries = async () => {
    try {
      const { data, error } = await supabase
        .from('breweries')
        .select(`
          *,
          products(count),
          reviews(rating)
        `)
        .eq('is_active', true)
        .eq('is_featured', true)
        .limit(6);

      if (error) throw error;

      const breweriesWithRatings = data.map(brewery => ({
        ...brewery,
        average_rating: brewery.reviews.length > 0
          ? brewery.reviews.reduce((sum, review) => sum + review.rating, 0) / brewery.reviews.length
          : 0,
        review_count: brewery.reviews.length,
        product_count: brewery.products[0]?.count || 0
      }));

      setBreweries(breweriesWithRatings);
    } catch (error) {
      console.error('Error fetching breweries:', error);
      setBreweries(mockBreweries);
    } finally {
      setLoading(false);
    }
  };

  const mockBreweries = [
    {
      id: 1,
      name: 'Cervejaria Artesanal São Paulo',
      slug: 'artesanal-sp',
      description: 'Cervejas artesanais premium com ingredientes selecionados',
      image: 'https://images.unsplash.com/photo-1571613316887-6f8d5cbf7ef7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      logo: 'https://images.unsplash.com/photo-1571613316887-6f8d5cbf7ef7?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
      address: 'Vila Madalena, São Paulo - SP',
      average_rating: 4.8,
      review_count: 124,
      product_count: 15,
      delivery_time: '30-45 min',
      delivery_fee: 8.90,
      is_open: true,
      plan_type: 'pro'
    },
    {
      id: 2,
      name: 'Hoppy Beer House',
      slug: 'hoppy-beer',
      description: 'Especializada em IPAs e cervejas lupuladas',
      image: 'https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      logo: 'https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
      address: 'Pinheiros, São Paulo - SP',
      average_rating: 4.6,
      review_count: 89,
      product_count: 22,
      delivery_time: '25-40 min',
      delivery_fee: 6.50,
      is_open: true,
      plan_type: 'pro'
    },
    {
      id: 3,
      name: 'Tradicional Brewing Co.',
      slug: 'tradicional-brewing',
      description: 'Receitas tradicionais alemãs e belgas',
      image: 'https://images.unsplash.com/photo-1436076863939-06870fe779c2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      logo: 'https://images.unsplash.com/photo-1436076863939-06870fe779c2?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80',
      address: 'Centro, São Paulo - SP',
      average_rating: 4.7,
      review_count: 156,
      product_count: 18,
      delivery_time: '35-50 min',
      delivery_fee: 7.90,
      is_open: false,
      plan_type: 'free'
    }
  ];

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="cervejarias" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-dark-900 mb-4">
              Cervejarias em Destaque
            </h2>
            <p className="text-lg text-dark-600 max-w-2xl mx-auto">
              Descubra as melhores cervejarias artesanais da sua região.
              Peça online e receba em casa com qualidade garantida.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {breweries.map((brewery, index) => (
            <motion.div
              key={brewery.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card hover:shadow-large transition-all duration-300 transform hover:-translate-y-1 cursor-pointer"
              onClick={() => navigate(`/cervejaria/${brewery.slug}`)}
            >
              <div className="relative">
                <img src={brewery.image} alt={brewery.name} className="w-full h-48 object-cover" />
                <div className="absolute top-4 left-4">
                  <span className={`badge ${brewery.is_open ? 'badge-success' : 'badge-error'}`}>
                    {brewery.is_open ? 'Aberto' : 'Fechado'}
                  </span>
                </div>
                {brewery.plan_type === 'pro' && (
                  <div className="absolute top-4 right-4">
                    <span className="badge bg-primary-100 text-primary-800">
                      PRO
                    </span>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <img src={brewery.logo} alt={`${brewery.name} logo`} className="w-12 h-12 rounded-lg object-cover" />
                    <div>
                      <h3 className="font-semibold text-lg text-dark-900 leading-tight">
                        {brewery.name}
                      </h3>
                      <div className="flex items-center space-x-1 mt-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium text-dark-700">
                          {brewery.average_rating.toFixed(1)}
                        </span>
                        <span className="text-sm text-dark-500">
                          ({brewery.review_count})
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-dark-600 text-sm mb-4 line-clamp-2">
                  {brewery.description}
                </p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-dark-600">
                    <MapPin className="w-4 h-4" />
                    <span>{brewery.address}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-dark-600">
                    <Clock className="w-4 h-4" />
                    <span>{brewery.delivery_time}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-dark-600">
                    <Truck className="w-4 h-4" />
                    <span>Entrega R$ {brewery.delivery_fee?.toFixed(2)}</span>
                  </div>
                </div>

                <div className="text-sm text-dark-500 mb-4">
                  {brewery.product_count} produtos disponíveis
                </div>

                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/cervejaria/${brewery.slug}`);
                  }}
                >
                  Ver Menu
                </Button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/cervejarias')}
          >
            Ver Todas as Cervejarias
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturedBreweries;